import React, { useContext, useEffect, useState, useMemo, useRef, useCallback } from "react";
import { StatusText, FreightStatusDots, FreightStatusDot } from "./style"

import { Eye, Pencil, Trash2, CheckCircle, Printer } from 'lucide-react';
import { printRoutePdf } from '../../../../components/PDF/Router/printRoutePdf';
import { RouterContext, Router } from "../../../../contexts/RouterContext";
import SectionContext from "../../../../contexts/SectionContext";
import axios from '../../../../config/axios';
import { useNavigate, useOutletContext } from "react-router-dom";
import { RouterOutletType } from "../index2";
import { FeedbackContext } from "../../../../contexts/FeedbackContext";
import TableFiltersContext from "../../../../contexts/TableFiltersContext"; 
import { OverflowCell } from '../../../../components/OverflowCell';
import styled from 'styled-components';
import {
    StandardDataTable,
    useColumnsState,
    StandardColumn,
} from '../../../../components/StandardPage';

const routesScreenKey = 'router_routes';

const TableRoot = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-direction: column;
`;

const dataFormatter = (isoDateString: string): string => {
    if (!isoDateString) return '';
    const dateUtc = new Date(isoDateString);
    if (isNaN(dateUtc.getTime())) return '';
    const year = dateUtc.getUTCFullYear();
    const month = dateUtc.getUTCMonth() + 1;
    const day = dateUtc.getUTCDate();
    const formattedMonth = month < 10 ? `0${month}` : month;
    const formattedDay = day < 10 ? `0${day}` : day;
    return `${formattedDay}/${formattedMonth}/${year}`;
};

const mapStatus = (status: string): string => {
    if (status === 'A programar' || status === 'Rota programada') return 'pending'; 
    if (status === 'Rota cancelado') return 'cancel';
    if (status === 'Rota concluida' ) return 'completed';
    return '';
};

const buildVehicleInfo = (vehicle: Router['Vehicle']) => {
    if (!vehicle) return '-';
    const parts = [vehicle.model, vehicle.year, vehicle.restriction].filter(Boolean);
    return parts.join(' - ');
};

const formatRouteFreights = (route: Router): string => {
    const freights = route.Freight || (route as any).freight || [];
    if (!freights.length) return '-';
    return freights
        .map((f: any, idx: number) => {
            const client = f.Company?.companyName || f.companyName || '';
            const order = f.orderNumber ? `Pedido: ${f.orderNumber}` : '';
            const type = f.type || '';
            const details = [client, order, type].filter(Boolean).join(' - ');
            return details ? `${idx + 1}. ${details}` : `${idx + 1}. Frete #${f.id}`;
        })
        .join('; ');
};

type FreightStatusKind = 'pending' | 'notDelivered' | 'completed';

interface FreightStatusCounts {
    pending: number;
    notDelivered: number;
    completed: number;
}

// Mesma classificação usada no app: Entregue/Coletado = concluído,
// "Não Entregue" = não entregue, qualquer outro status = pendente.
const classifyFreightStatus = (status?: string | null): FreightStatusKind => {
    if (status === 'Entregue' || status === 'Coletado') return 'completed';
    if (status === 'Não Entregue') return 'notDelivered';
    return 'pending';
};

const countFreightStatuses = (route: Router): FreightStatusCounts => {
    const freights: any[] = route.Freight || (route as any).freight || [];
    const counts: FreightStatusCounts = { pending: 0, notDelivered: 0, completed: 0 };
    freights.forEach((f) => {
        counts[classifyFreightStatus(f?.status)] += 1;
    });
    return counts;
};

const FREIGHT_STATUS_LABELS: Record<FreightStatusKind, string> = {
    pending: 'Pendentes',
    notDelivered: 'Não entregues',
    completed: 'Concluídos',
};

const FREIGHT_STATUS_ORDER: FreightStatusKind[] = ['pending', 'notDelivered', 'completed'];

const formatFreightStatusCounts = (route: Router): string => {
    const counts = countFreightStatuses(route);
    const total = counts.pending + counts.notDelivered + counts.completed;
    if (!total) return '-';
    return FREIGHT_STATUS_ORDER
        .map((kind) => `${FREIGHT_STATUS_LABELS[kind]}: ${counts[kind]}`)
        .join(' | ');
};

const FreightStatusSummary: React.FC<{ route: Router }> = ({ route }) => {
    if (route.status === 'Rota concluida') return <span>-</span>;
    const counts = countFreightStatuses(route);
    const total = counts.pending + counts.notDelivered + counts.completed;
    if (!total) return <span>-</span>;
    return (
        <FreightStatusDots title={formatFreightStatusCounts(route)}>
            {FREIGHT_STATUS_ORDER.map((kind) => (
                <FreightStatusDot key={kind} className={kind}>
                    <i />
                    {counts[kind]}
                </FreightStatusDot>
            ))}
        </FreightStatusDots>
    );
};

const buildRoutesColumns = (): StandardColumn<Router>[] => [
    { id: 'id', label: 'Número', accessor: (route) => route.id },
    { id: 'origin', label: 'Origem', accessor: (route) => route.origin },
    {
        id: 'status',
        label: 'Status',
        accessor: (route) => route.status,
        cell: (route) => (
            <StatusText className={mapStatus(route.status)}>
                {route.status}
            </StatusText>
        ),
    },
    { id: 'vehiclePlate', label: 'Placa', accessor: (route) => route.Vehicle?.plate || '-' },
    {
        id: 'vehicleInfo',
        label: 'Veículo',
        accessor: (route) => buildVehicleInfo(route.Vehicle),
        cell: (route) => (
            <OverflowCell text={buildVehicleInfo(route.Vehicle)} maxWidth={180} />
        ),
    },
    {
        id: 'responsible',
        label: 'Responsável da Rota',
        accessor: (route) => route.responsible || '-',
        cell: (route) => (
            <OverflowCell text={route.responsible || '-'} maxWidth={160} />
        ),
    },
    {
        id: 'companyDestination',
        label: 'Voltar à origem',
        accessor: (route) => (route.companyDestination ? 'Sim' : 'Não'),
    },
    {
        id: 'freights',
        label: 'Fretes selecionados',
        accessor: formatRouteFreights,
        cell: (route) => (
            <OverflowCell text={formatRouteFreights(route)} maxWidth={240} />
        ),
    },
    {
        id: 'freightStatus',
        label: 'Fretes por status',
        accessor: formatFreightStatusCounts,
        disableTooltip: true,
        cell: (route) => <FreightStatusSummary route={route} />,
    },
    { id: 'date', label: 'Data', accessor: (route) => dataFormatter(route.date) },
    { id: 'track', label: 'Tempo de trajeto', accessor: (route) => route.track ? route.track : '-' },
];

interface routerTableType {
    onChange?: () => void
}

const RouterTable: React.FC<routerTableType> = () => {
    const { search, setTabIndex, appliedFilters, refresh, setLoadingHeader } = useOutletContext<RouterOutletType>();

    const { setRouter } = useContext(RouterContext);
    const { setRouterSection } = useContext(SectionContext);
    const [routes, setRoutes] = useState<Router[]>([]); 
    const [totalRoutes, setTotalRoutes] = useState<number | null>(null);
    const [page, setPage] = useState(0); 
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    
    const { setFeedback } = useContext(FeedbackContext);
    const navigate = useNavigate(); 
    const { routerStatusFilter } = useContext(TableFiltersContext);

    const columns = useMemo(() => buildRoutesColumns(), []);
    const { columnsState, handleColumnsChange, handleRestoreColumns } =
        useColumnsState(routesScreenKey, columns);

    useEffect(() => {
        setTabIndex(1);
        setRouterSection('router');
    }, []);

    const handleEdit = async (id: number) => {
        try {
            const fetchedRouter = await getRoute(id);
            setRouter(fetchedRouter);
            setRouterSection('newRouter'); 
            navigate('/panel/router/routers/edit');
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar roteiro para edição!',
                statusOk: false
            });
        }
    };
    
    const handleView = async (id: number) => {
        try {
            await getRoute(id); 
            navigate(`${id}`); 
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar detalhes do roteiro!',
                statusOk: false
            });
        }
    };

    const deleteRoute = async (id: number) => {
        try {
            await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/${id}`);
            setPage(0); 
            setHasMore(true); 
            getRoutes(0, routerStatusFilter, search); 
            setFeedback({
                isOpen: true,
                message: 'Rota deletada!',
                statusOk: true
            });
        } catch {
            setFeedback({
                isOpen: true,
                message: 'Erro ao deletar rota!',
                statusOk: false
            });
        }
    };
    
    const completeRoute = async (id: number) => {
        try {
            await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/${id}/completed`);
            setFeedback({
                isOpen: true,
                message: 'Rota completa!',
                statusOk: true
            });
            fetchInitialRoutes();
        } catch {
            setFeedback({
                isOpen: true,
                message: 'Erro ao concluir rota',
                statusOk: false
            });
        }
    };

    const handlePrint = async (id: number) => {
        try {
            const routeData = await getRoute(id);
            await printRoutePdf(routeData);
        } catch {
            setFeedback({
                isOpen: true,
                message: 'Erro ao preparar impressão do roteiro.',
                statusOk: false
            });
        }
    };

    const getRoute = async (id: number): Promise<Router> => { 
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/${id}`);
        const routeData = response.data as Router; 
        setRouter(routeData); 
        return routeData; 
    };

    const getRoutes = async (pageNumber: number, statusFilter: string | null = null, searchTerm: string = '') => {
        try {
            const params = new URLSearchParams();
            params.append('page', pageNumber.toString());
            params.append('size', '50');

            if (statusFilter && statusFilter !== 'Todos' && statusFilter !== '') {
                params.append('status', statusFilter);
            }

            if (searchTerm && searchTerm.trim() !== '') {
                params.append('search', searchTerm);
            }

            if (appliedFilters && appliedFilters.length > 0) {
                params.append('filters', JSON.stringify(appliedFilters));
            }

            const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route?${params.toString()}`;
            const response = await axios.get(url);
            const fetchedRoutes = response.data as Router[];

            try {
                const countParams = new URLSearchParams();
                if (statusFilter && statusFilter !== 'Todos' && statusFilter !== '') {
                    countParams.append('status', statusFilter);
                }
                if (searchTerm && searchTerm.trim() !== '') {
                    countParams.append('search', searchTerm);
                }
                if (appliedFilters && appliedFilters.length > 0) {
                    countParams.append('filters', JSON.stringify(appliedFilters));
                }

                const queryCount = countParams.toString() ? `?${countParams.toString()}` : '';
                const countUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/count${queryCount}`;
                const countResp = await axios.get(countUrl);
                
                setTotalRoutes(countResp.data?.total ?? null);
            } catch (err) {
                setTotalRoutes(null);
            }

            return fetchedRoutes;
        } catch (error) {
            console.error("Erro ao buscar rotas:", error);
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar rotas!',
                statusOk: false
            });
            return [];
        }
    };

    const fetchInitialRoutes = useCallback(async () => {
        setIsLoading(true);
        setLoadingHeader?.(true);
        try {
            setPage(0);
            setHasMore(true);
            const initialRoutes = await getRoutes(0, routerStatusFilter, search);
            const hasMoreData = initialRoutes.length >= 50;
            setRoutes(initialRoutes);
            setHasMore(hasMoreData);
        } finally {
            setIsLoading(false);
            setLoadingHeader?.(false);
        }
    }, [routerStatusFilter, search, appliedFilters, setLoadingHeader]);

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (refresh) fetchInitialRoutes();
    }, [refresh]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchInitialRoutes();
        }, 100);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchInitialRoutes]);

    const loadMoreRoutes = async () => {
        if (!hasMore || isLoadingMore) return;

        setIsLoadingMore(true);
        try {
            const nextPage = page + 1;
            const newRoutes = await getRoutes(nextPage, routerStatusFilter, search);
            
            const hasMoreData = newRoutes.length >= 50;
            const dataToShow = newRoutes;
            
            setHasMore(hasMoreData);
            setRoutes(prevRoutes => [...prevRoutes, ...dataToShow]);
            setPage(nextPage);
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar mais rotas',
                statusOk: false
            });
        } finally {
            setIsLoadingMore(false);
        }
    };

    return (
        <TableRoot>
            <StandardDataTable
                tableName="Route"
                columns={columns}
                data={routes}
                columnsState={columnsState}
                onColumnsChange={handleColumnsChange}
                onRestoreColumns={handleRestoreColumns}
                totalCount={totalRoutes}
                onLoadMore={loadMoreRoutes}
                hasMore={hasMore}
                isLoading={isLoading || isLoadingMore}
                countLabel='rotas'
                exportFileName='rotas'
                emptyMessage={search ? `Nenhuma rota encontrada para "${search}"` : 'Nenhuma rota cadastrada.'}
                rowActions={(routerItem) => [
                    { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleView(routerItem.id!) },
                    { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEdit(routerItem.id!) },
                    { label: 'Imprimir', icon: <Printer size={16} />, onClick: () => handlePrint(routerItem.id!) },
                    ...(routerItem.status !== 'Rota concluida' && routerItem.status !== 'Rota cancelado'
                        ? [{ label: 'Concluir rota', icon: <CheckCircle size={16} />, onClick: () => completeRoute(routerItem.id!) }]
                        : []),
                    { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => deleteRoute(routerItem.id!) },
                ]}
            />
        </TableRoot>
    );
}

export default RouterTable;
